// HTML imprimible de un recibo de servicios (funcion pura, sin dependencias del navegador).
// Es un recibo, no una factura: la facturacion se hace por otro medio y nunca lleva IVA.
// Texto oscuro sobre blanco; los fondos son solo apoyo (se lee igual sin graficos de fondo).

const esc = v => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const dinero = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(v) || 0);
const fechaLarga = iso => {
  const d = iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`) : new Date();
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
};
const fechaCorta = iso => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString('es-CO');
};

const LOGO = '<svg class="logo" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><rect width="64" height="64" rx="14" fill="#08293f"/><path d="M15 47 27 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/><path d="M31 47 43 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/></svg>';

const CSS = `
@page{size:letter;margin:14mm 14mm}
*{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Segoe UI',Arial,sans-serif;color:#0b2538;font-size:12px;line-height:1.4}
.logo{width:46px;height:46px;flex:none}
.cab{display:flex;align-items:center;gap:14px;padding-bottom:12px;border-bottom:3px solid #00b8e3;margin-bottom:16px}
.cab .t{flex:1}
.marca{font-size:9px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#0087a8}
.cab h1{font-size:22px;color:#08293f;font-weight:800;line-height:1.1;margin-top:2px}
.cab .c{font-size:10.5px;color:#4a6577;margin-top:3px}
.der{text-align:right}
.der .tit{font-size:24px;font-weight:800;color:#08293f;letter-spacing:.06em}
.der .num{font-size:12px;font-weight:700;color:#0087a8;margin-top:2px}
.der .f{font-size:11px;color:#4a6577;margin-top:2px}
.cli{border:1px solid #cfdde8;border-radius:4px;padding:10px 14px;background:#f6f9fb;margin-bottom:16px;display:grid;grid-template-columns:1.4fr 1fr 1.2fr;gap:6px 18px}
.cli small{display:block;font-size:8.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#4a6577}
.cli b{display:block;font-size:12.5px;color:#08293f;margin-top:1px;word-break:break-word}
.cli .full{grid-column:1/-1}
table{width:100%;border-collapse:collapse;table-layout:fixed;margin-bottom:12px}
th{font-size:9px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#08293f;text-align:left;padding:8px 8px;background:#e8f1f7;border-bottom:2px solid #08293f}
td{padding:8px 8px;border-bottom:1px solid #dbe6ee;vertical-align:top;font-size:11.5px;overflow-wrap:anywhere}
tr{break-inside:avoid}
.r{text-align:right}th.r{text-align:right}
.tot{margin-left:auto;width:54%;border:1px solid #cfdde8;border-radius:4px;overflow:hidden;margin-bottom:16px}
.tot div{display:flex;justify-content:space-between;padding:7px 12px;font-size:12px;border-bottom:1px solid #dbe6ee}
.tot div:last-child{border-bottom:none}
.tot .g{background:#08293f;color:#fff;font-size:15px;font-weight:800;padding:10px 12px}
.tot .p{color:#166534;font-weight:700}.tot .d{color:#9a3412;font-weight:700}
.obs{border:1px solid #dbe6ee;border-radius:4px;padding:9px 12px;margin-bottom:22px;font-size:11.5px}
.obs small{display:block;font-size:8.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#4a6577;margin-bottom:2px}
.firmas{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:46px}
.firmas div{border-top:1px solid #08293f;padding-top:5px;text-align:center;font-size:10px;color:#4a6577}
.pie{margin-top:22px;border-top:1px solid #dbe6ee;padding-top:6px;font-size:9.5px;color:#6b8191;display:flex;justify-content:space-between}
`;

// items: [{ fecha, servicio, solicitante, valor, estado }]. Un recibo no es factura: no lleva IVA.
export function construirHtmlRecibo({ cliente, infoCliente = {}, items = [], fecha, numero = '', formaPago = '', observaciones = '' }) {
  const total = items.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const pagado = items.filter(i => i.estado === 'PAGADO').reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const pendiente = total - pagado;
  const hayPersona = items.some(i => i.solicitante);
  const cols = hayPersona
    ? '<colgroup><col style="width:13%"><col style="width:42%"><col style="width:25%"><col style="width:20%"></colgroup>'
    : '<colgroup><col style="width:14%"><col style="width:64%"><col style="width:22%"></colgroup>';
  const filas = items.map(i => `<tr>
    <td>${esc(fechaCorta(i.fecha))}</td>
    <td>${esc(i.servicio || '—')}</td>
    ${hayPersona ? `<td>${esc(i.solicitante || '')}</td>` : ''}
    <td class="r"><b>${esc(dinero(i.valor))}</b></td>
  </tr>`).join('');
  const datos = [
    infoCliente?.nit ? `<div><small>NIT / CC</small><b>${esc(infoCliente.nit)}</b></div>` : '',
    infoCliente?.correo ? `<div><small>Correo</small><b>${esc(infoCliente.correo)}</b></div>` : '',
    infoCliente?.direccion ? `<div class="full"><small>Dirección</small><b>${esc(infoCliente.direccion)}</b></div>` : '',
  ].join('');
  return `<!doctype html><html lang="es"><head><meta charset="UTF-8"><title>Recibo ${esc(cliente)}</title><style>${CSS}</style></head><body>
  <div class="cab">${LOGO}
    <div class="t"><div class="marca">Servicios de tecnología</div><h1>AGE Informática</h1><div class="c">info@ageinformatica.com · 313 3030874</div></div>
    <div class="der"><div class="tit">RECIBO</div>${numero ? `<div class="num">No. ${esc(numero)}</div>` : ''}<div class="f">${esc(fechaLarga(fecha))}</div></div>
  </div>
  <div class="cli"><div><small>Cliente</small><b>${esc(cliente)}</b></div>${datos}</div>
  <table>${cols}
    <thead><tr><th>Fecha</th><th>Servicio</th>${hayPersona ? '<th>Solicitante</th>' : ''}<th class="r">Valor</th></tr></thead>
    <tbody>${filas}</tbody>
  </table>
  <div class="tot">
    ${pendiente > 0 && pagado > 0 ? `<div class="p"><span>Pagado</span><span>${esc(dinero(pagado))}</span></div><div class="d"><span>Saldo pendiente</span><span>${esc(dinero(pendiente))}</span></div>` : ''}
    <div class="g"><span>TOTAL</span><span>${esc(dinero(total))}</span></div>
  </div>
  ${formaPago || observaciones ? `<div class="obs">${formaPago ? `<small>Forma de pago</small>${esc(formaPago)}` : ''}${formaPago && observaciones ? '<div style="height:6px"></div>' : ''}${observaciones ? `<small>Observaciones</small>${esc(observaciones)}` : ''}</div>` : ''}
  <div class="firmas"><div>Entregado por · AGE Informática</div><div>Recibido por · ${esc(cliente)}</div></div>
  <div class="pie"><span>AGE Informática · Este recibo no constituye factura de venta.</span><span>${esc(fechaLarga(fecha))}</span></div>
  <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;
}
